import React from "react";
import styled from "styled-components";
import Input from "components/atoms/Input";
import Button from "components/atoms/Button";
import Text from "components/atoms/Text";
import { COLOR_SYSTEM } from "components/design/design-system";

interface YmydSignupFormProps {
  formData: {
    email: string;
    password: string;
    passwordConfirm: string;
    name: string;
    phone: string;
    company: string;
    license?: string;
  };
  passwordError?: string | null;
  passwordConfirmError?: string | null;
  onPasswordChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPasswordConfirmChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
}

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const InfoText = styled(Text)`
  color: ${COLOR_SYSTEM.get("Gray")[500]};
  font-size: 12px;
  margin-top: 4px;
`;

const DisabledInputWrapper = styled.div`
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 28px; /* 라벨 높이를 고려한 위치 */
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(248, 249, 250, 0.6);
    pointer-events: none;
    border-radius: 4px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 1.5rem;
`;

const YmydSignupForm: React.FC<YmydSignupFormProps> = ({
  formData,
  passwordError,
  passwordConfirmError,
  onPasswordChange,
  onPasswordConfirmChange,
  onSubmit,
  onCancel,
}) => {
  const [email, setEmail] = React.useState(formData.email || '');

  // formData.email이 변경되면 email state도 업데이트
  React.useEffect(() => {
    if (formData.email) {
      setEmail(formData.email);
    }
  }, [formData.email]);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };

  const handleSubmitClick = () => {
    // 이메일을 변경했다면 formData 업데이트
    if (email !== formData.email) {
      formData.email = email;
    }
    // Form 이벤트를 시뮬레이션
    const fakeEvent = { preventDefault: () => {} } as React.FormEvent;
    onSubmit(fakeEvent);
  };

  return (
    <FormContainer>
      {/* 이름 - 읽기 전용 */}
      <FormGroup>
        <DisabledInputWrapper>
          <Input
            type="text"
            label="이름"
            value={formData.name || ''}
            onChange={() => {}} // 빈 핸들러
            disabled
            style={{ width: "100%" }}
          />
        </DisabledInputWrapper>
      </FormGroup>

      {/* 연락처 - 읽기 전용 */}
      <FormGroup>
        <DisabledInputWrapper>
          <Input
            type="text"
            label="연락처"
            value={formData.phone || ''}
            onChange={() => {}} // 빈 핸들러
            disabled
            style={{ width: "100%" }}
          />
        </DisabledInputWrapper>
      </FormGroup>

      {/* 이메일 - 수정 가능 */}
      <FormGroup>
        <Input
          type="email"
          label="이메일"
          value={email}
          onChange={handleEmailChange}
          placeholder="이메일을 입력해주세요"
          required
          style={{ width: "100%" }}
        />
        <InfoText textType="Caption">PhAI 서비스 로그인에 사용할 이메일입니다.</InfoText>
      </FormGroup>

      {/* 비밀번호 */}
      <FormGroup>
        <Input
          type="password"
          label="비밀번호"
          value={formData.password}
          onChange={onPasswordChange}
          placeholder="8자 이상 입력해주세요"
          error={!!passwordError}
          hint={passwordError || undefined}
          required
          style={{ width: "100%" }}
        />
      </FormGroup>

      {/* 비밀번호 확인 */}
      <FormGroup>
        <Input
          type="password"
          label="비밀번호 확인"
          value={formData.passwordConfirm}
          onChange={onPasswordConfirmChange}
          placeholder="비밀번호를 다시 입력해주세요"
          error={!!passwordConfirmError}
          hint={passwordConfirmError || undefined}
          required
          style={{ width: "100%" }}
        />
      </FormGroup>

      <ButtonGroup>
        <Button
          type="Text"
          size="Large"
          variant="Contained"
          color="Primary"
          onClick={handleSubmitClick}
          style={{ flex: 1 }}
        >
          회원가입
        </Button>
        <Button
          type="Text"
          size="Large"
          variant="Outlined"
          color="Secondary"
          onClick={onCancel}
          style={{ flex: 1 }}
        >
          취소
        </Button>
      </ButtonGroup>
    </FormContainer>
  );
};

export default YmydSignupForm;